import { UnknownAction } from "@reduxjs/toolkit";
import {
  FETCH_EXPERIENCES_STORYBOARD_LISTS_REQUEST,
  FETCHING_EXPERIENCES_STORYBOARD_LISTS_SUCCEEDED,
  FETCHING_EXPERIENCES_STORYBOARD_LISTS_FAILED,
  CREATE_EXPERIENCES_STORYBOARD_LIST_REQUEST,
  CREATING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED,
  CREATING_EXPERIENCES_STORYBOARD_LIST_FAILED,
  RESET_EXPERIENCES_STORYBOARD_LIST_CREATE,
  UPDATE_EXPERIENCES_STORYBOARD_LIST_REQUEST,
  UPDATING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED,
  UPDATING_EXPERIENCES_STORYBOARD_LIST_FAILED,
  DELETE_EXPERIENCES_STORYBOARD_LIST_REQUEST,
  DELETING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED,
  DELETING_EXPERIENCES_STORYBOARD_LIST_FAILED,
} from "../../actions/actionTypes";
import { StoryboardListItem } from "src/types/experiences/storyboardLists";

interface CreateState {
  creating: boolean;
  created: boolean;
  item: StoryboardListItem | null;
  error: any;
}

interface UpdateState {
  id: string;
  updating: boolean;
  updated: boolean;
  item: StoryboardListItem | null;
  error: any;
}

interface DeleteState {
  id: string;
  deleting: boolean;
  deleted: boolean;
  error: any;
}

interface StoryboardListsState {
  loading: boolean;
  fetched: boolean;
  items: StoryboardListItem[];
  create: CreateState;
  // Keyed by folder id so each folder tracks its own rename/delete status.
  update: Record<string, UpdateState>;
  deletion: Record<string, DeleteState>;
  error: any;
}

const initialState: StoryboardListsState = {
  loading: false,
  fetched: false,
  items: [],
  create: {
    creating: false,
    created: false,
    item: null,
    error: null,
  },
  update: {},
  deletion: {},
  error: null,
};

export const storyboardListsReducer = (
  state: StoryboardListsState = initialState,
  action: UnknownAction
): StoryboardListsState => {
  switch (action.type) {

    // ─── Fetch ──────────────────────────────────────────────────────────────
    case FETCH_EXPERIENCES_STORYBOARD_LISTS_REQUEST:
      return { ...state, loading: true, fetched: false, error: null };

    case FETCHING_EXPERIENCES_STORYBOARD_LISTS_SUCCEEDED:
      return {
        ...state,
        loading: false,
        fetched: true,
        items: action.payload as StoryboardListItem[],
        error: null,
      };

    case FETCHING_EXPERIENCES_STORYBOARD_LISTS_FAILED:
      return { ...state, loading: false, error: action.payload };

    // ─── Create ─────────────────────────────────────────────────────────────
    case CREATE_EXPERIENCES_STORYBOARD_LIST_REQUEST:
      return {
        ...state,
        create: { creating: true, created: false, item: null, error: null },
      };

    case CREATING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED: {
      const newItem = action.payload as StoryboardListItem;
      return {
        ...state,
        items: [...state.items, newItem],
        create: { creating: false, created: true, item: newItem, error: null },
      };
    }

    case CREATING_EXPERIENCES_STORYBOARD_LIST_FAILED:
      return {
        ...state,
        create: { creating: false, created: false, item: null, error: action.payload },
      };

    case RESET_EXPERIENCES_STORYBOARD_LIST_CREATE:
      return {
        ...state,
        create: { creating: false, created: false, item: null, error: null },
      };

    // ─── Update (rename) ─────────────────────────────────────────────────────
    case UPDATE_EXPERIENCES_STORYBOARD_LIST_REQUEST: {
      const { id } = action.payload as { id: string };
      return {
        ...state,
        update: {
          ...state.update,
          [id]: { id, updating: true, updated: false, item: null, error: null },
        },
      };
    }

    case UPDATING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED: {
      const updated = action.payload as StoryboardListItem;
      return {
        ...state,
        items: state.items.map((item) => (item.id === updated.id ? updated : item)),
        update: {
          ...state.update,
          [updated.id]: {
            id: updated.id,
            updating: false,
            updated: true,
            item: updated,
            error: null,
          },
        },
      };
    }

    case UPDATING_EXPERIENCES_STORYBOARD_LIST_FAILED: {
      const { id, error } = action.payload as { id: string; error: any };
      return {
        ...state,
        update: {
          ...state.update,
          [id]: { id, updating: false, updated: false, item: null, error },
        },
      };
    }

    // ─── Delete ──────────────────────────────────────────────────────────────
    case DELETE_EXPERIENCES_STORYBOARD_LIST_REQUEST: {
      const { id } = action.payload as { id: string };
      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: { id, deleting: true, deleted: false, error: null },
        },
      };
    }

    case DELETING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED: {
      const { id } = action.payload as { id: string };
      return {
        ...state,
        items: state.items.filter((item) => item.id !== id),
        deletion: {
          ...state.deletion,
          [id]: { id, deleting: false, deleted: true, error: null },
        },
      };
    }

    case DELETING_EXPERIENCES_STORYBOARD_LIST_FAILED: {
      const { id, error } = action.payload as { id: string; error: any };
      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: { id, deleting: false, deleted: false, error },
        },
      };
    }

    default:
      return state;
  }
};
